import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

// Login screen -- updated 060: Phone+OTP, email+password, and Google Sign-In

type AuthMethod = "phone" | "email";

export default function LoginPage() {
  const { login, sendOtp, verifyOtp, otpSent, signInWithGoogle, signInWithFacebook, resetPassword, error } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState<AuthMethod>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function handleSendOtp(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await sendOtp(phoneNumber);
    } catch {
      // error is set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await verifyOtp(otpCode);
      navigate("/");
    } catch {
      // error is set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEmailLogin(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch {
      // error is set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    setSubmitting(true);
    try {
      await signInWithGoogle();
      navigate("/");
    } catch {
      // error is set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  async function handleFacebookSignIn() {
    setSubmitting(true);
    try {
      await signInWithFacebook();
      navigate("/");
    } catch {
      // error is set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await resetPassword(email);
      setResetSent(true);
    } catch {
      // error is set in auth context
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-page="login" data-state={submitting ? "submitting" : otpSent ? "otp-verify" : "ready"} className="flex min-h-screen flex-col items-center justify-center p-4">
      {/* Mobile branding header (full-screen login per the design); desktop keeps
          the card's own "Sign in" header. */}
      <div className="mb-6 flex flex-col items-center gap-1.5 md:hidden">
        <span className="mb-1 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-2xl font-bold text-primary-foreground">D</span>
        <h1 className="text-xl font-extrabold">Welcome to Dugads</h1>
        <p className="text-sm text-muted-foreground">School sport, all in one place.</p>
      </div>
      <Card className="w-full max-w-sm border-0 bg-transparent shadow-none md:border md:bg-background md:shadow-sm">
        <CardHeader className="hidden md:flex">
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            {otpSent
              ? "Enter the code sent to your phone"
              : "Choose how you'd like to sign in"}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 md:px-6">
          <div className="flex flex-col gap-4">
            {error && (
              <p data-error className="text-sm text-destructive">{error}</p>
            )}

            {!otpSent ? (
              <>
                {/* Method toggle */}
                <div data-region="method-toggle" className="flex rounded-md border">
                  <button
                    type="button"
                    data-action="select-method"
                    data-value="phone"
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-l-md transition-colors ${
                      method === "phone"
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    }`}
                    onClick={() => setMethod("phone")}
                  >
                    Phone
                  </button>
                  <button
                    type="button"
                    data-action="select-method"
                    data-value="email"
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-r-md transition-colors ${
                      method === "email"
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    }`}
                    onClick={() => setMethod("email")}
                  >
                    Email
                  </button>
                </div>

                {method === "phone" ? (
                  /* Phone number entry */
                  <form data-region="form" data-form="phone-login" onSubmit={handleSendOtp} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="phone">Phone number</Label>
                      <Input
                        id="phone"
                        data-field="phone"
                        type="tel"
                        placeholder="+27821234567"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Use international format with country code
                      </p>
                    </div>
                    <Button data-action="submit" type="submit" disabled={submitting}>
                      {submitting ? "Sending..." : "Send OTP"}
                    </Button>
                  </form>
                ) : resetMode ? (
                  /* Password reset */
                  resetSent ? (
                    <div className="flex flex-col gap-4 text-center">
                      <p className="text-sm">Password reset email sent to <span className="font-medium">{email}</span>. Check your inbox.</p>
                      <Button variant="outline" onClick={() => { setResetMode(false); setResetSent(false); }}>
                        Back to Sign In
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="reset-email">Email</Label>
                        <Input
                          id="reset-email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                      <Button type="submit" disabled={submitting}>
                        {submitting ? "Sending..." : "Send Reset Link"}
                      </Button>
                      <button
                        type="button"
                        onClick={() => setResetMode(false)}
                        className="text-center text-sm text-primary hover:underline"
                      >
                        Back to Sign In
                      </button>
                    </form>
                  )
                ) : (
                  /* Email+password entry */
                  <form data-region="form" data-form="email-login" onSubmit={handleEmailLogin} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        data-field="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          onClick={() => setResetMode(true)}
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <Input
                        id="password"
                        data-field="password"
                        type="password"
                        placeholder="Minimum 8 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                      />
                    </div>
                    <Button data-action="submit" type="submit" disabled={submitting}>
                      {submitting ? "Signing in..." : "Sign In"}
                    </Button>
                    <p className="text-center text-sm">
                      Don't have an account?{" "}
                      <Link to="/register" className="text-primary hover:underline">
                        Create account
                      </Link>
                    </p>
                  </form>
                )}

                {/* Divider */}
                <div className="relative flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {/* Social Sign-In */}
                <Button
                  data-action="google-sign-in"
                  type="button"
                  variant="outline"
                  onClick={handleGoogleSignIn}
                  disabled={submitting}
                  className="gap-2"
                >
                  <svg className="size-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Continue with Google
                </Button>
                {/* Facebook sign-in hidden until provider config is verified
                <Button
                  data-action="facebook-sign-in"
                  type="button"
                  variant="outline"
                  onClick={handleFacebookSignIn}
                  disabled={submitting}
                  className="gap-2"
                >
                  <svg className="size-4" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2" />
                  </svg>
                  Continue with Facebook
                </Button>
                */}
              </>
            ) : (
              /* OTP verification */
              <form data-region="form" data-form="otp-verify" onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    id="otp"
                    data-field="otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    required
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    Sent to {phoneNumber}
                  </p>
                </div>
                <Button data-action="submit" type="submit" disabled={submitting}>
                  {submitting ? "Verifying..." : "Verify"}
                </Button>
                <Button
                  data-action="change-number"
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setOtpCode("");
                    window.location.reload();
                  }}
                >
                  Use a different number
                </Button>
              </form>
            )}
          </div>

          {/* Invisible reCAPTCHA container -- required by Firebase phone auth */}
          <div id="recaptcha-container" />
        </CardContent>
      </Card>
    </div>
  );
}
